import React from "react";
import { Grid as SpectrumGrid } from "@adobe/react-spectrum";
import { spacing } from "../../stitches.config";
import { StyledSection, StyledSections } from "../Common/__styles__/Layout";

type SpectrumGridProps = React.ComponentProps<typeof SpectrumGrid>;

type ColumnsInput = number | { base?: number; M?: number; L?: number };

export const Sections: React.FC<{
  children: React.ReactNode;
  gap?: (typeof spacing)[keyof typeof spacing];
  as?: keyof JSX.IntrinsicElements | React.ElementType;
  onSubmit?: () => void;
}> = ({ children, gap = spacing.xl, as = "div", onSubmit }) => {
  return (
    <StyledSections
      style={{ display: "flex", flexDirection: "column", gap: gap.value }}
      as={as}
      onSubmit={onSubmit}
    >
      {children}
    </StyledSections>
  );
};

export const Section: React.FC<{
  children: React.ReactNode;
  gap?: (typeof spacing)[keyof typeof spacing];
  flexDirection?: "column" | "row";
  style?: React.CSSProperties;
}> = ({ children, gap = spacing.m, flexDirection = "column", style }) => {
  return (
    <StyledSection
      style={{ display: "flex", flexDirection, gap: gap.value, ...style }}
    >
      {children}
    </StyledSection>
  );
};

const getSpectrumColumns = (
  columns: ColumnsInput,
): SpectrumGridProps["columns"] => {
  if (typeof columns === "number") {
    return new Array(columns).fill("1fr");
  }

  const responsiveColumns: {
    base?: string[];
    M?: string[];
    L?: string[];
  } = {};

  if (columns.base !== undefined) {
    responsiveColumns.base = new Array(columns.base).fill("1fr");
  }
  if (columns.M !== undefined) {
    responsiveColumns.M = new Array(columns.M).fill("1fr");
  }
  if (columns.L !== undefined) {
    responsiveColumns.L = new Array(columns.L).fill("1fr");
  }

  return responsiveColumns;
};

type GridProps = {
  columns?: ColumnsInput;
  gap?: (typeof spacing)[keyof typeof spacing];
};

export const Grid = ({
  children,
  columns = 1,
  gap = spacing.m,
}: React.PropsWithChildren<GridProps>) => {
  return (
    <SpectrumGrid columns={getSpectrumColumns(columns)} gap={gap.value}>
      {children}
    </SpectrumGrid>
  );
};
